<template>
  <div
    ref="tableRootRef"
    class="modern-table"
    :class="{
      'modern-table--banner-rows': useBannerRowLayout,
      'modern-table--full-width': !useBannerRowLayout,
      'modern-table--fill': scrollHeightMode === 'flex' && !isTableEmpty,
      'modern-table--empty': isTableEmpty,
      'modern-table--rows-expanded': rowsExpanded,
      'modern-table--row-context-actions': showRowContextActions,
      'modern-table--row-context-overflow': showRowContextActions && rowContextVisibility.showOverflow,
      'modern-table--remote-sort': remoteSort,
      'skip-default-styles': skipDefaultStyles,
      [tableWidthScopeClass]: !useBannerRowLayout,
    }"
    :style="fillMinHeightStyle"
  >
    <component v-if="columnWidthsCss" is="style">{{ columnWidthsCss }}</component>
    <div v-if="props.showHeader && showControlPanel" class="table-chrome-bar table-chrome-bar--summary">
      <MPagination
        v-model="localMeta"
        variant="summary"
        :element-id="elementId"
        :selected-count="tableSelectedRowCount"
        class="table-pagination top"
        @change="onPaginationChange"
      />
      <button
        v-if="hasMultiLineTextColumn"
        type="button"
        class="table-expand-rows-btn"
        :class="{ 'table-expand-rows-btn--active': rowsExpanded }"
        :title="t('ui.toggleRowHeight')"
        :aria-label="t('ui.toggleRowHeight')"
        :aria-pressed="rowsExpanded"
        @click="toggleRowsExpanded"
      >
        <i class="pi pi-arrows-v" aria-hidden="true" />
      </button>
    </div>

    <div class="modern-table__body-wrap">
      <button
        v-if="showControlPanel && !showRowContextActions"
        type="button"
        class="table-settings-btn table-settings-btn--thead"
        :aria-label="t('ui.tableSettings')"
        @click="settingsDialogVisible = true"
      >
        <i class="pi pi-cog" />
      </button>

    <!-- DataTable -->
    <DataTable :value="itemsWithIds" v-model:selection="internalSelection" :loading="loading"
      :lazy="remoteSort"
      :rows="localMeta.itemsPerPage" :striped-rows="false" :scrollable="isScrollable"
      :scrollHeight="resolvedScrollHeight"
      :selectOnRowClick="false" data-key="__syntheticId" :row-style="resolveRowInlineStyle"
      @row-click="onRowClick" @row-dblclick="onRowDoubleClick"
      @contextmenu="onContextMenu" v-model:context-menu-selection="selectedItem"
      :editMode="props.cellEditable ? 'cell' : undefined"
      :class="{ 'table-without-header': props.showTableHeader === false }"
      @cell-edit-complete="onCellEditComplete"
      @cell-edit-cancel="onCellEditCancel">

      <!-- Custom Context Menu -->
      <ContextMenu ref="contextMenu" :model="contextMenuModel" @hide="onContextMenuHide(); selectedItem = null" />
      <Column v-if="withCheckbox && selectionMode === 'multiple'" :selection-mode="selectionMode"
        header-class="p-selection-column"
        body-class="p-selection-column"
        headerStyle="width: 3rem">
      </Column>

      <Column v-for="column in visibleHeaders" :key="column.value" :field="column.value"
        :sortable="!remoteSort && column.isSort && sortable" :header-style="getColumnStyle(column)"
        :header-class="getColumnHeaderClass(column)"
        :body-style="getColumnStyle(column)"
        :body-class="getColumnBodyClass(column)"
        :pt="getColumnBodyCellPt(column)">
        <template #body="slotProps">
          <TableCellRenderer
            :item="getOriginalItem(slotProps.data)"
            :column="column"
            :item-value="itemValue"
            :table-field-name="tableName"
            :row-presentation="resolveRowPresentation(slotProps.data)"
            :rows-expanded="rowsExpanded"
            @action="onAction"
          />
        </template>

        <template v-if="isEditableColumn(column) && !isBooleanColumn(column)" #editor="slotProps">
          <EditableTableListboxCellEditor
            v-if="isListboxColumn(column)"
            :row-data="slotProps.data"
            :column="column"
            :table-field-name="tableName"
          />
          <input
            v-else
            :value="getEditorDisplayValue(slotProps.data[column.value], column)"
            @input="onEditorTextInput(slotProps.data, column, $event)"
            class="table-cell-editor"
            type="text"
          >
        </template>

        <template #header>
          <div
            class="header-content"
            :class="{
              'inline-actions-header': column.isInlineAction,
              'header-content--sortable': !column.isInlineAction && column.isSort && sortable,
            }"
            @click.stop="!column.isInlineAction && column.isSort && sortable ? onSort(column) : null"
          >
            <span v-if="!column.isInlineAction" class="header-text">{{ column.name }}</span>
            <i
              v-if="remoteSort && !column.isInlineAction && column.isSort && sortable"
              class="sort-icon pi"
              :class="sortIconClass(column)"
              aria-hidden="true"
            />
          </div>
          <div
            v-if="canResizeColumn(column)"
            class="m-table-column-resizer"
            @mousedown.prevent="onColumnResizeStart($event, column)"
          />
        </template>
      </Column>

      <Column
        v-if="showRowContextActions"
        header-class="table-row-context-actions-col"
        body-class="table-row-context-actions-col"
        :header-style="rowContextActionsColumnStyle"
        :body-style="rowContextActionsColumnStyle"
      >
        <template #header>
          <button
            v-if="showControlPanel"
            type="button"
            class="table-settings-btn table-settings-btn--in-actions"
            :aria-label="t('ui.tableSettings')"
            @click.stop="settingsDialogVisible = true"
          >
            <i class="pi pi-cog" />
          </button>
        </template>
        <template #body="slotProps">
          <TableRowContextActions
            :key="getRowContextCacheKey(slotProps.data)"
            v-if="rowContextShowPrimary(slotProps.data)"
            :primary-label="rowContextPrimaryPresentation(slotProps.data).label"
            :primary-icon="rowContextPrimaryPresentation(slotProps.data).icon"
            :show-overflow="rowContextShowOverflow(slotProps.data)"
            :overflow-label="t('ui.moreActions')"
            @shown="onRowContextShown(slotProps.data)"
            @primary="onRowContextPrimary($event, slotProps.data)"
            @overflow="onRowContextOverflow($event, slotProps.data)"
          />
        </template>
      </Column>

      <template #empty>
        <div class="empty-state">
          <i class="empty-icon pi pi-table" />
          <p class="empty-text">Нет данных для отображения</p>
        </div>
      </template>
    </DataTable>
    </div>

    <!-- Swing SOUTH: JJPaginationPanel вне JScrollPane (скрыт, если страница одна). -->
    <MPagination
      v-if="showControlPanel && items.length > 0 && hasBottomPagination"
      v-model="localMeta"
      variant="footer"
      :element-id="elementId"
      :selected-count="tableSelectedRowCount"
      class="table-pagination bottom"
      @change="onPaginationChange"
    />

    <!-- Модальное окно настройки таблицы -->
    <Dialog
      v-if="showControlPanel"
      v-model:visible="settingsDialogVisible"
      modal
      :header="t('ui.tableSettings')"
      :style="{ width: '40rem' }"
      :data-testid="settingsDialogTestId"
    >
      <span class="block mb-4">Столбцы для отображения:</span>
      <div class="block mb-2">
        <div v-for="column in headers" :key="column.value" class="flex items-center mb-2">
          <label>
            <Checkbox v-model="selectedColumns" :inputId="column.value" name="columns" :value="column.value" />
            &nbsp;{{ column.name }}
          </label>
        </div>
      </div>
      <div class="flex justify-end gap-2">
        <Button type="button" :label="t('ui.save')" @click="onSaveSettings(); settingsDialogVisible = false"></Button>
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from '@/common/composables/useI18n'
import MPagination from '@/components/ui/MPagination.vue'
import Dialog from 'primevue/dialog';
import Column from 'primevue/column'
import ContextMenu from 'primevue/contextmenu'
import DataTable from 'primevue/datatable'
import { computed, ref, watch, onBeforeUnmount, onMounted, nextTick } from 'vue'
import { setType } from '@/common/utils/typedValueSerializer'
import TableCellRenderer from './TableCellRenderer.vue'
import TableRowContextActions from './TableRowContextActions.vue'
import EditableTableListboxCellEditor from './EditableTableListboxCellEditor.vue'
import {
  filterPlatformContextMenuItems,
  resolveTableRowContextActionsColumnWidthPx,
  resolveTableRowContextActionsVisibility,
  resolveTableRowContextOverride,
  resolveTableRowPrimaryActionPresentation,
  tableRowContextOverridesEqual,
  createTableRowContextPreviewGate,
} from '@/common/utils/tableRowContextActions'
import {
  buildTreeTableRowSwingPresentation,
  type TreeTableRowSwingPresentation,
} from '@/common/utils/treeTableRowSwingPresentation'
import { isWideListboxColumnWidth } from '@/common/utils/listboxTableCell'
import { parseTableColumnWidthPx } from '@/common/utils/parseTableColumnWidth'
import { resolveSwingTableColumnWidths } from '@/common/utils/resolveSwingTableColumnWidths'
import { PLATFORM_TABLE_FILL_MIN_BODY_PX } from '@/common/utils/resolveTableScrollHeight'
import { useTableContextMenu } from '@/common/composables/useTableContextMenu'
import { buildStableTestId } from '@/common/utils/testId'
import type { ElementSettingsParams } from '@/stores/settings';
import { startWorkflowMeasure } from '@/common/utils/performanceTrace'
import { checkboxSelectionAfterItemsUpdate } from '@/common/utils/pruneTableSelectionBySyntheticIds'
import {
  clearEditableCellDraft,
  peekEditableCellDraft,
  rememberEditableCellDraft,
  resolvePendingEditableCellCommit,
} from '@/common/utils/editableTablePendingCommit'
import { isEditableTableCellClick, readRowBooleanCellValue } from '@/common/utils/editableTableRowClick'
import { configService } from '@/common/utils/clientConfig'

interface TableHeader {
  name: string
  value: string
  isSort?: boolean
  width?: string
  constantColumnWidth?: boolean
  format?: any
  type?: string
  isInlineAction?: boolean
  isReadOnly?: boolean
  columnElementId?: string
  listboxCatalog?: Array<{ code: string; image?: string; text?: string }>
}

interface TableMeta {
  page: number
  pages: number
  itemsPerPage: number
  sort: {
    column: string
    direction: 'asc' | 'desc'
  }
  totalItems?: number
}

interface Props {
  items?: any[]
  headers?: TableHeader[]
  meta?: TableMeta
  itemValue?: string
  sortable?: boolean
  /** TABLE/TREETABLE `isSort=true`: Swing remoteSorting — reload, без локального sort PrimeVue. */
  remoteSort?: boolean
  showControlPanel?: boolean
  withCheckbox?: boolean
  selected?: any[]
  selectionMode?: 'single' | 'multiple'
  loading?: boolean
  contextMenuItems?: any[]
  elementId?: string | number | null
  selectedColumns?: string[] | null
  columnWidths?: Record<string, number> | null
  cellEditable?: boolean
  tableName?: string
  showHeader?: boolean
  showTableHeader?: boolean
  scrollHeight?: string | null
  skipDefaultStyles?: boolean
  /**
   * Async-хук до show контекстного меню (onInlineMenuShow / visibility).
   * Если задан — вызывается вместо emit; Vue emit не ждёт parent listener.
   */
  beforeContextMenuShow?: (event: {
    selectedIdx: number
    target: Event
    column: string
    data: any
    preview?: boolean
  }) => void | any[] | Promise<void | any[]>
  previewInlineMenu?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  headers: () => [],
  meta: () => ({
    page: 1,
    pages: 1,
    itemsPerPage: 50,
    sort: { column: '', direction: 'asc' },
    totalItems: 0,
    totalItemsBackEnd: 0
  }),
  itemValue: 'value',
  sortable: true,
  remoteSort: false,
  showControlPanel: true,
  withCheckbox: false,
  selected: () => [],
  selectionMode: 'multiple',
  loading: false,
  cellEditable: false,
  tableName: '',
  showHeader: true,
  showTableHeader: true,
  scrollHeight: null,
  skipDefaultStyles: false,
  previewInlineMenu: false,
})

const { t } = useI18n()

const isTableEmpty = computed(() => !Array.isArray(props.items) || props.items.length === 0)

const hasMultiLineTextColumn = computed(() =>
  (props.headers ?? []).some(
    (column) => String(column?.type ?? '').trim().toUpperCase() === 'MULTILINETEXT',
  ),
)

/** Swing TableSizeManager.isExpanded — разворот высоты строк MultiLineText */
const rowsExpanded = ref(false)

const toggleRowsExpanded = () => {
  rowsExpanded.value = !rowsExpanded.value
}

const scrollHeightMode = computed(() => {
  const value = props.scrollHeight
  if (value === 'flex') return 'flex'
  if (value == null || value === '') return 'none'
  return 'fixed'
})

/** Swing AUTO leftover: grow into leftover, never below 2 body rows + thead (2717). */
const fillMinHeightStyle = computed(() => {
  if (scrollHeightMode.value !== 'flex' || isTableEmpty.value) return undefined
  return {
    '--platform-table-fill-min-body': `${PLATFORM_TABLE_FILL_MIN_BODY_PX}px`,
  }
})

/**
 * Flex leftover: scroll on .modern-table__body-wrap (CSS), not PrimeVue
 * scrollHeight=flex — that viewport stays content-sized and never shows a bar.
 */
const isScrollable = computed(() => {
  if (isTableEmpty.value) return false
  return scrollHeightMode.value === 'fixed'
})

const resolvedScrollHeight = computed(() => {
  if (isTableEmpty.value) return undefined
  if (scrollHeightMode.value === 'fixed') {
    return props.scrollHeight ?? undefined
  }
  return undefined
})

// видимость модалки настроек таблицы
const settingsDialogVisible = ref(false);

const settingsDialogTestId = computed(() => {
  const id = props.elementId
  if (id == null || String(id).trim() === '') return undefined
  return buildStableTestId('element', [id, 'dialog'])
})

// выбранные к видимости столбцы
const selectedColumns = ref(
  props.selectedColumns?.length
    ? [...props.selectedColumns]
    : props.headers.map(item => item.value)
);

const emit = defineEmits<{
  'update:selected': [selected: any[]]
  'update:meta': [meta: TableMeta]
  'sort': [meta: TableMeta]
  'update:modelValue': [value: any]
  'change': [value: any]
  'action': [data: any]
  'click': [event: { selectedIdx: number; column: string; data: any }]
  'dblclick': [event: { selectedIdx: number; column: string; data: any }]
  'contextmenu': [event: { selectedIdx: number; target: Event; column: string; data: any }]
  'saveSetting': [event: { paramName: keyof ElementSettingsParams; paramValue: ElementSettingsParams[keyof ElementSettingsParams] }]
}>()

const localMeta = ref({ ...props.meta })
const selectedItem = ref<unknown>(null)

const contextMenuItemsAfterShow = ref<any[] | null>(null)

const {
  contextMenu,
  contextMenuModel,
  handleContextMenu: handleTableContextMenu,
  prepareContextMenu,
  showPreparedContextMenu,
  onContextMenuHide,
  contextRowData
} = useTableContextMenu({
  contextMenuItems: computed(() => props.contextMenuItems || []),
  getContextMenuItems: () => contextMenuItemsAfterShow.value ?? props.contextMenuItems ?? [],
  headers: computed(() => props.headers || []),
  onEmit: async (event) => {
    if (event.preview !== true) {
      contextMenuItemsAfterShow.value = null
    }
    const rowIndex =
      typeof event.selectedIdx === 'number' && event.selectedIdx >= 0
        ? event.selectedIdx
        : resolveRowIndex(event.data)
    const originalData = resolveOriginalRow(event.data, rowIndex)
    const payload = {
      selectedIdx: rowIndex,
      target: event.target,
      column: event.column,
      data: originalData,
      preview: event.preview === true,
    }
    if (typeof props.beforeContextMenuShow === 'function') {
      const result = await props.beforeContextMenuShow(payload)
      if (Array.isArray(result)) {
        if (event.preview !== true) {
          contextMenuItemsAfterShow.value = result
        }
        rememberRowContextOverride(originalData, result)
        return result
      }
    } else if (event.preview !== true) {
      emit('contextmenu', payload)
    }
  }
})

const platformContextMenuItems = computed(() =>
  filterPlatformContextMenuItems(props.contextMenuItems),
)

const rowContextVisibility = computed(() =>
  resolveTableRowContextActionsVisibility(platformContextMenuItems.value),
)

const rowActionsColumnPinned = ref(false)
watch(
  () => rowContextVisibility.value.showColumn,
  (show) => {
    if (show) rowActionsColumnPinned.value = true
  },
  { immediate: true },
)

const rowContextOverrideByKey = ref<
  Record<string, { showPrimary: boolean; showOverflow: boolean; icon?: string; label?: string }>
>({})

const showRowContextActions = computed(
  () =>
    rowActionsColumnPinned.value ||
    rowContextVisibility.value.showColumn ||
    Object.values(rowContextOverrideByKey.value).some((o) => o.showPrimary),
)

const rowContextActionsColumnWidthPx = computed(() => {
  if (!showRowContextActions.value) return 0
  return resolveTableRowContextActionsColumnWidthPx(rowContextVisibility.value.showOverflow)
})

const rowContextActionsColumnStyle = computed(() => ({
  width: `${rowContextActionsColumnWidthPx.value}px`,
  minWidth: `${rowContextActionsColumnWidthPx.value}px`,
  maxWidth: `${rowContextActionsColumnWidthPx.value}px`,
}))

const getRowContextCacheKey = (rowData: any): string => {
  const synthetic = rowData?.__syntheticId
  if (typeof synthetic === 'string' && synthetic) return synthetic
  return String(resolveRowIndex(rowData))
}

const rowContextShowOverflow = (rowData: any): boolean => {
  const override = rowContextOverrideByKey.value[getRowContextCacheKey(rowData)]
  if (override) return override.showOverflow
  return rowContextVisibility.value.showOverflow
}

const rowContextShowPrimary = (rowData: any): boolean => {
  const override = rowContextOverrideByKey.value[getRowContextCacheKey(rowData)]
  if (override) return override.showPrimary
  return rowContextVisibility.value.showColumn
}

const rowContextPrimaryPresentation = (rowData: any) => {
  const override = rowContextOverrideByKey.value[getRowContextCacheKey(rowData)]
  const first = platformContextMenuItems.value[0]
  const item = override
    ? { label: override.label ?? first?.label, icon: override.icon ?? first?.icon }
    : first
  return resolveTableRowPrimaryActionPresentation(item)
}

const rememberRowContextOverride = (rowData: any, items: any[]) => {
  const key = getRowContextCacheKey(rowData)
  const next = resolveTableRowContextOverride(items)
  if (tableRowContextOverridesEqual(rowContextOverrideByKey.value[key], next)) return
  rowContextOverrideByKey.value = {
    ...rowContextOverrideByKey.value,
    [key]: next,
  }
}

const rowContextPreviewGate = createTableRowContextPreviewGate()

const selectRowForContextAction = (rowData: any) => {
  selectedItem.value = rowData
  if (props.selectionMode === 'single') {
    internalSelection.value = rowData
  } else if (props.selectionMode === 'multiple' && !props.withCheckbox) {
    internalSelection.value = [rowData]
  }
}

const getRowContextActionEventPayload = (rowData: any) => {
  const idx = resolveRowIndex(rowData)
  const originalItem = resolveOriginalRow(rowData, idx)
  return { idx, originalItem }
}

const onRowContextPrimary = async (event: MouseEvent, rowData: any) => {
  const { idx, originalItem } = getRowContextActionEventPayload(rowData)
  selectRowForContextAction(rowData)
  const items = await prepareContextMenu(event, originalItem, undefined, {
    withCheckbox: props.withCheckbox,
    selectedIdx: idx,
  })
  if (items == null) return
  rememberRowContextOverride(rowData, items)
  if (!items.length) return
  const first = items[0]
  if (typeof first?.command === 'function') {
    await first.command({
      originalEvent: event,
      data: originalItem,
      selectedIdx: idx,
    })
  }
}

const onRowContextShown = (rowData: any) => {
  if (!props.previewInlineMenu) return
  const key = getRowContextCacheKey(rowData)
  if (rowContextOverrideByKey.value[key]) return
  rowContextPreviewGate.enqueue(key, async () => {
    if (rowContextOverrideByKey.value[key]) return
    const { idx, originalItem } = getRowContextActionEventPayload(rowData)
    const items = await prepareContextMenu(undefined, originalItem, undefined, {
      withCheckbox: props.withCheckbox,
      selectedIdx: idx,
      preview: true,
    })
    if (items) rememberRowContextOverride(rowData, items)
  })
}

const onRowContextOverflow = async (event: MouseEvent, rowData: any) => {
  const { idx, originalItem } = getRowContextActionEventPayload(rowData)
  selectRowForContextAction(rowData)
  const items = await prepareContextMenu(event, originalItem, undefined, {
    withCheckbox: props.withCheckbox,
    selectedIdx: idx,
  })
  if (items == null) return
  rememberRowContextOverride(rowData, items)
  showPreparedContextMenu(event)
}

const getRowIdentityValue = (item: any): unknown => {
  const raw = item?._rowData ?? item
  return (
    item?.__syntheticId ??
    item?._id ??
    item?.id ??
    item?.fields?._id?.value ??
    item?.fields?.id?.value ??
    item?._versionId ??
    item?.fields?._versionId?.value ??
    raw?._id?.value ??
    raw?._id ??
    raw?.id?.value ??
    raw?.id ??
    raw?.fields?._id?.value ??
    raw?.fields?.id?.value ??
    raw?._versionId?.value ??
    raw?._versionId ??
    raw?.fields?._versionId?.value
  )
}

const itemsWithIds = computed(() => {
  const endMeasure = startWorkflowMeasure('MTable.itemsWithIds', {
    tableName: props.tableName,
    itemCount: props.items.length,
  })
  try {
    return props.items.map((item, index) => {
    if (item && typeof item === 'object') {
      const identity = getRowIdentityValue(item)
      const syntheticId =
        identity !== undefined && identity !== null && identity !== ''
          ? `__synthetic_${String(identity)}`
          : `__synthetic_index_${index}`

      return {
        ...item,
        __syntheticId: syntheticId
      }
    }
    return {
      __value: item,
      __syntheticId: `__synthetic_index_${index}_primitive`
    }
  })
  } finally {
    endMeasure()
  }
})

const getOriginalItem = (item: any) => {
  if (!item || typeof item !== 'object') return item

  if ('__value' in item && Object.keys(item).length === 2 && '__syntheticId' in item) {
    return item.__value
  }

  const { __syntheticId, ...originalItem } = item
  return originalItem
}

const resolveRowIndex = (rowData: any) => {
  if (rowData == null || !props.items?.length) return -1

  if (typeof rowData?.__syntheticId === 'string') {
    const syntheticMatch = rowData.__syntheticId.match(/^__synthetic_(\d+)_/)
    if (syntheticMatch) {
      const syntheticIndex = Number(syntheticMatch[1])
      if (!Number.isNaN(syntheticIndex) && syntheticIndex >= 0 && syntheticIndex < props.items.length) {
        return syntheticIndex
      }
    }
  }

  const originalRow = getOriginalItem(rowData)
  let idx = props.items.findIndex((item: any) => item === originalRow)
  if (idx !== -1) return idx

  idx = props.items.findIndex((item: any) => {
    const source = getOriginalItem(item)
    return (
      source === originalRow ||
      source?._rowData === originalRow?._rowData ||
      (source?._id != null &&
        originalRow?._id != null &&
        String(source._id) === String(originalRow._id)) ||
      (source?.fields?._id?.value != null &&
        originalRow?.fields?._id?.value != null &&
        String(source.fields._id.value) === String(originalRow.fields._id.value))
    )
  })

  return idx
}

const resolveOriginalRow = (rowData: any, selectedIdx?: number) => {
  if (
    typeof selectedIdx === 'number' &&
    selectedIdx >= 0 &&
    Array.isArray(props.items) &&
    selectedIdx < props.items.length
  ) {
    return props.items[selectedIdx]
  }

  const idx = resolveRowIndex(rowData)
  if (idx >= 0 && idx < props.items.length) {
    return props.items[idx]
  }

  return rowData != null ? getOriginalItem(rowData) : null
}

const internalSelection = ref()
let isSyncingFromProps = false

function isSameSelectionRow(item: unknown, rowData: unknown): boolean {
  if (item == null || rowData == null) return false
  if (item === rowData) return true
  const itemId = (item as { __syntheticId?: string }).__syntheticId
  const rowDataId = (rowData as { __syntheticId?: string }).__syntheticId
  if (itemId != null && rowDataId != null) {
    return itemId === rowDataId
  }
  return JSON.stringify(getOriginalItem(item)) === JSON.stringify(getOriginalItem(rowData))
}

function toggleCheckboxRowSelection(rowData: unknown) {
  const current = Array.isArray(internalSelection.value) ? [...internalSelection.value] : []
  const index = current.findIndex((item) => isSameSelectionRow(item, rowData))
  if (index >= 0) {
    current.splice(index, 1)
  } else {
    current.push(rowData)
  }
  internalSelection.value = current
}

watch(internalSelection, (newSelection) => {
  if (isSyncingFromProps) return

  let selectedArray: any[] = []

  if (props.selectionMode === 'multiple') {
    selectedArray = Array.isArray(newSelection)
      ? newSelection.map(item => getOriginalItem(item))
      : []
  } else {
    selectedArray = newSelection ? [getOriginalItem(newSelection)] : []
  }

  emit('update:selected', selectedArray)
}, { deep: true })

watch(itemsWithIds, (nextItems, previousItems) => {
  if (!(props.withCheckbox && props.selectionMode === 'multiple')) return
  const current = Array.isArray(internalSelection.value) ? internalSelection.value : []
  if (current.length === 0) return
  const nextSelection = checkboxSelectionAfterItemsUpdate(current, previousItems, nextItems)
  // Same store rows: a checkbox click rebuilt the wrappers. Do not emit [],
  // that would wipe registerTableSelection while the boxes stay painted.
  const keepChecks = nextSelection.length > 0
  if (keepChecks) isSyncingFromProps = true
  internalSelection.value = nextSelection
  if (keepChecks) {
    Promise.resolve().then(() => {
      isSyncingFromProps = false
    })
  }
})

const totalRecords = computed(() => {
  const totalItems = Number(props.meta.totalItems)
  if (Number.isFinite(totalItems)) {
    return totalItems
  }

  const pages = Number(props.meta.pages)
  const itemsLength = Array.isArray(props.items) ? props.items.length : 0
  if (Number.isFinite(pages) && pages > 0) {
    return itemsLength * pages
  }

  return itemsLength
})

/** Как MGroupTable: нижний переключатель страниц только при pages > 1. */
const hasBottomPagination = computed(() => {
  const pages = Number(localMeta.value?.pages ?? props.meta?.pages ?? 1)
  return Number.isFinite(pages) && pages > 1
})

const normalizeColumnType = (type: unknown): string => String(type ?? '').trim().toUpperCase()

const tableRootRef = ref<HTMLElement | null>(null)
const tableWidthPx = ref(0)
let tableResizeObserver: ResizeObserver | null = null

const RESIZE_MIN_COLUMN_PX = 15
const SELECTION_COLUMN_WIDTH_PX = 40

const hasSelectionColumn = computed(
  () => props.withCheckbox && props.selectionMode === 'multiple',
)

const tableSelectedRowCount = computed<number | undefined>(() => {
  if (!hasSelectionColumn.value) return undefined
  const selection = internalSelection.value
  if (Array.isArray(selection)) return selection.length
  return selection ? 1 : 0
})

const tableDomId = computed(() => {
  if (props.elementId) return String(props.elementId).replace(/[^a-zA-Z0-9_-]/g, '_')
  if (props.tableName) return String(props.tableName).replace(/[^a-zA-Z0-9_-]/g, '_')
  return 'default'
})

const tableWidthScopeClass = computed(() => `mtable-w-${tableDomId.value}`)

const userColumnWidths = ref<Record<string, number>>({})
const liveResizeWidths = ref<Record<string, number> | null>(null)

type ColumnResizeState = {
  columnIndex: number
  partnerIndex: number
  startX: number
  startWidths: Record<string, number>
}

const columnResizeState = ref<ColumnResizeState | null>(null)

watch(
  () => props.columnWidths,
  (saved) => {
    if (columnResizeState.value) return
    userColumnWidths.value =
      saved && typeof saved === 'object' ? { ...saved } : {}
  },
  { immediate: true, deep: true },
)

const measureTableWidth = () => {
  const el = tableRootRef.value
  if (!el) return
  const width = el.getBoundingClientRect().width
  if (width > 0) {
    tableWidthPx.value = Math.round(width)
  }
}

onMounted(() => {
  measureTableWidth()
  nextTick(measureTableWidth)

  if (typeof ResizeObserver !== 'undefined' && tableRootRef.value) {
    tableResizeObserver = new ResizeObserver(() => {
      measureTableWidth()
    })
    tableResizeObserver.observe(tableRootRef.value)
  }
})

onBeforeUnmount(() => {
  if (tableResizeObserver) {
    tableResizeObserver.disconnect()
    tableResizeObserver = null
  }
  if (clickTimer) {
    clearTimeout(clickTimer)
    clickTimer = null
  }
  stopColumnResizeListeners()
})

const resolvedColumnWidths = computed(() => {
  if (useBannerRowLayout.value) return {}

  const width = tableWidthPx.value
  if (width <= 0) return {}

  const availableWidth = Math.max(
    0,
    width
      - (hasSelectionColumn.value ? SELECTION_COLUMN_WIDTH_PX : 0)
      - rowContextActionsColumnWidthPx.value,
  )

  return resolveSwingTableColumnWidths(
    visibleHeaders.value.map((column) => ({
      value: column.value,
      width: column.width,
      constantColumnWidth: column.constantColumnWidth,
      type: column.type,
    })),
    availableWidth,
    userColumnWidths.value,
  )
})

const effectiveColumnWidths = computed(() => {
  if (liveResizeWidths.value) return liveResizeWidths.value
  return resolvedColumnWidths.value
})

const columnWidthsCss = computed(() => {
  if (useBannerRowLayout.value) return ''

  const widths = effectiveColumnWidths.value
  const cols = visibleHeaders.value
  const scope = `.${tableWidthScopeClass.value}`
  const checkboxOffset = hasSelectionColumn.value ? 1 : 0
  let css = ''

  // Pin checkbox column even before tableWidthPx is measured. Otherwise
  // table-layout:fixed + hidden thead split leftover width 50/50 (form 2811).
  if (hasSelectionColumn.value) {
    const px = SELECTION_COLUMN_WIDTH_PX
    css += `${scope} .p-datatable-table > thead > tr > th:nth-child(1),
${scope} .p-datatable-table > tbody > tr > td:nth-child(1),
${scope} .p-datatable-table > thead > tr > th[data-p-selection-column="true"],
${scope} .p-datatable-table > tbody > tr > td[data-p-selection-column="true"] {
  width: ${px}px !important;
  max-width: ${px}px !important;
  min-width: ${px}px !important;
  box-sizing: border-box;
}
`
  }

  if (showRowContextActions.value) {
    const px = rowContextActionsColumnWidthPx.value
    css += `${scope} .p-datatable-table > thead > tr > th.table-row-context-actions-col,
${scope} .p-datatable-table > tbody > tr > td.table-row-context-actions-col {
  width: ${px}px !important;
  max-width: ${px}px !important;
  min-width: ${px}px !important;
  box-sizing: border-box;
}
`
  }

  if (!cols.length) return css

  cols.forEach((column, index) => {
    const px = widths[column.value]
    if (px == null || px <= 0) return

    const nth = index + 1 + checkboxOffset
    const mobileMin = Math.max(px, 112)

    css += `${scope} .p-datatable-table > thead > tr > th:nth-child(${nth}),
${scope} .p-datatable-table > tbody > tr > td:nth-child(${nth}) {
  width: ${px}px !important;
  max-width: ${px}px !important;
  min-width: ${px}px !important;
  overflow: hidden;
  box-sizing: border-box;
}
@media (max-width: 768px) {
  ${scope} .p-datatable-table > thead > tr > th:nth-child(${nth}),
  ${scope} .p-datatable-table > tbody > tr > td:nth-child(${nth}) {
    width: ${mobileMin}px !important;
    min-width: ${mobileMin}px !important;
    max-width: none !important;
  }
}\n`
  })

  css += `@media (max-width: 768px) {
  ${scope}.modern-table--full-width .p-datatable-table {
    table-layout: auto !important;
    width: max-content !important;
    min-width: 100% !important;
  }
}\n`

  return css
})

const visibleHeaders = computed(() =>
  props.headers.filter((column) => selectedColumns.value.includes(column.value)),
)

const isWideListboxImageColumn = (column: TableHeader) => {
  const columnType = normalizeColumnType(column.type)
  const isListbox = columnType === 'LISTBOX' || columnType === 'LIST_BOX'
  const widthPx = parseTableColumnWidthPx(column.width)
  return isListbox && isWideListboxColumnWidth(widthPx)
}

/** ListBox icon + InlineAction title — banner rows как в Swing (кнопка на всю ширину). */
const useBannerRowLayout = computed(
  () =>
    visibleHeaders.value.some((column) => column.isInlineAction) &&
    visibleHeaders.value.some((column) => isWideListboxImageColumn(column)),
)

const isBooleanColumn = (column: TableHeader | undefined) => {
  const columnType = normalizeColumnType(column?.type)
  return columnType === 'BOOLEAN' || columnType === 'CHECKBOX'
}

const isListboxColumn = (column: TableHeader | undefined) => {
  const columnType = normalizeColumnType(column?.type)
  return (
    columnType === 'LISTBOX' ||
    columnType === 'LIST_BOX' ||
    columnType === 'COMBOBOX' ||
    columnType === 'COMBO'
  )
}

const isEditableColumn = (column: TableHeader) => props.cellEditable && !column.isInlineAction && !column.isReadOnly

const getColumnBodyClass = (column: TableHeader) => {
  const classes: string[] = []
  if (isEditableColumn(column)) classes.push('table-column-editable')
  if (useBannerRowLayout.value) {
    if (isWideListboxImageColumn(column)) classes.push('table-listbox-icon-column')
    if (column.isInlineAction) classes.push('table-inline-action-fill')
  }
  return classes.length ? classes.join(' ') : undefined
}

const resolveRowBackingData = (rowData: unknown): Record<string, unknown> | null => {
  const item = getOriginalItem(rowData as Record<string, unknown>)
  if (!item || typeof item !== 'object') return null
  const backing = (item as { _rowData?: unknown })._rowData
  if (backing && typeof backing === 'object') {
    return backing as Record<string, unknown>
  }
  return item as Record<string, unknown>
}

const resolveRowPresentation = (rowData: unknown): TreeTableRowSwingPresentation | undefined => {
  const backing = resolveRowBackingData(rowData)
  if (!backing) return undefined
  const columnIds = visibleHeaders.value.map((column) => column.value)
  return buildTreeTableRowSwingPresentation(backing, columnIds)
}

const resolveRowInlineStyle = (rowData: Record<string, unknown>) => {
  const presentation = resolveRowPresentation(rowData)
  const style = presentation?.rowInlineStyle
  if (!style || Object.keys(style).length === 0) return undefined
  // SPD: soft server row__color so zebra (#E8E8E8 / #FFE4C4) is less harsh than solid paint.
  if (configService.isSpd() && style.backgroundColor) {
    return {
      ...style,
      backgroundColor: `color-mix(in srgb, ${style.backgroundColor} 45%, white)`,
    }
  }
  return style
}

const getColumnBodyCellPt = (column: TableHeader) =>
  column.type ? { bodyCell: { 'data-type': column.type } } : undefined

const getColumnStyle = (column: TableHeader) => {
  const base = {
    cursor: column.isSort && props.sortable ? 'pointer' : 'default',
  }

  if (useBannerRowLayout.value) {
    if (isWideListboxImageColumn(column)) {
      const px = parseTableColumnWidthPx(column.width)
      if (px == null) return base
      return { ...base, width: `${px}px`, maxWidth: `${px}px` }
    }

    if (column.isInlineAction) {
      return { ...base, width: 'auto' }
    }
  }

  return base
}

const findNextResizableColumnIndex = (columnIndex: number): number | null => {
  const cols = visibleHeaders.value
  for (let i = columnIndex + 1; i < cols.length; i += 1) {
    if (!cols[i].constantColumnWidth) {
      return i
    }
  }
  return null
}

const canResizeColumn = (column: TableHeader) => {
  if (useBannerRowLayout.value || column.constantColumnWidth || column.isInlineAction) {
    return false
  }

  const columnIndex = visibleHeaders.value.findIndex((col) => col.value === column.value)
  return columnIndex >= 0 && findNextResizableColumnIndex(columnIndex) != null
}

const snapshotColumnWidths = (): Record<string, number> => {
  const widths: Record<string, number> = {}
  for (const column of visibleHeaders.value) {
    const px = effectiveColumnWidths.value[column.value]
    if (px != null && px > 0) {
      widths[column.value] = px
    }
  }

  if (Object.keys(widths).length >= visibleHeaders.value.length) {
    return widths
  }

  const root = tableRootRef.value
  if (!root) return widths

  const ths = root.querySelectorAll('.p-datatable-table > thead > tr > th')
  let dataColIndex = 0
  const hasCheckboxCol = hasSelectionColumn.value

  ths.forEach((th, thIndex) => {
    if (hasCheckboxCol && thIndex === 0) return
    if (th.classList.contains('table-row-context-actions-col')) return

    const column = visibleHeaders.value[dataColIndex]
    if (!column) return
    dataColIndex += 1

    const width = Math.round(th.getBoundingClientRect().width)
    if (width > 0) {
      widths[column.value] = width
    }
  })

  return widths
}

const stopColumnResizeListeners = () => {
  document.removeEventListener('mousemove', onColumnResizeMove)
  document.removeEventListener('mouseup', onColumnResizeEnd)
  columnResizeState.value = null
  document.body.style.removeProperty('user-select')
  document.body.style.removeProperty('cursor')
}

const onColumnResizeStart = (event: MouseEvent, column: TableHeader) => {
  measureTableWidth()

  const columnIndex = visibleHeaders.value.findIndex((col) => col.value === column.value)
  const partnerIndex = columnIndex >= 0 ? findNextResizableColumnIndex(columnIndex) : null
  if (columnIndex < 0 || partnerIndex == null) {
    return
  }

  const startWidths = snapshotColumnWidths()
  const currentColumn = visibleHeaders.value[columnIndex]
  const partnerColumn = visibleHeaders.value[partnerIndex]
  if (
    !currentColumn ||
    !partnerColumn ||
    startWidths[currentColumn.value] == null ||
    startWidths[partnerColumn.value] == null
  ) {
    return
  }

  liveResizeWidths.value = { ...startWidths }
  columnResizeState.value = {
    columnIndex,
    partnerIndex,
    startX: event.clientX,
    startWidths,
  }

  document.body.style.userSelect = 'none'
  document.body.style.cursor = 'col-resize'
  document.addEventListener('mousemove', onColumnResizeMove)
  document.addEventListener('mouseup', onColumnResizeEnd)
}

const onColumnResizeMove = (event: MouseEvent) => {
  const state = columnResizeState.value
  if (!state) return

  const cols = visibleHeaders.value
  const currentColumn = cols[state.columnIndex]
  const partnerColumn = cols[state.partnerIndex]
  if (!currentColumn || !partnerColumn) return

  const delta = event.clientX - state.startX
  const currentStart = state.startWidths[currentColumn.value]
  const partnerStart = state.startWidths[partnerColumn.value]
  if (currentStart == null || partnerStart == null) return

  const newCurrent = currentStart + delta
  const newPartner = partnerStart - delta

  if (newCurrent < RESIZE_MIN_COLUMN_PX || newPartner < RESIZE_MIN_COLUMN_PX) {
    return
  }

  const widths: Record<string, number> = { ...state.startWidths }
  widths[currentColumn.value] = Math.round(newCurrent)
  widths[partnerColumn.value] = Math.round(newPartner)
  liveResizeWidths.value = widths
}

const onColumnResizeEnd = () => {
  const hadResize = columnResizeState.value != null
  const finalWidths = liveResizeWidths.value

  stopColumnResizeListeners()
  liveResizeWidths.value = null

  if (!hadResize || !finalWidths) return

  userColumnWidths.value = { ...finalWidths }
  emit('saveSetting', {
    paramName: 'columnWidths',
    paramValue: { ...finalWidths },
  })
}

const getColumnHeaderClass = (column: TableHeader) => {
  const classes: string[] = []
  if (canResizeColumn(column)) classes.push('m-table-col-resizable')
  if (column.isSort && props.sortable && localMeta.value.sort.column === column.value) {
    classes.push('m-table-col-sorted')
  }
  return classes.length ? classes.join(' ') : undefined
}

const isTimeLikeColumn = (column: TableHeader | undefined) => {
  const columnType = normalizeColumnType(column?.type)
  return columnType === 'TIME' || columnType === 'DATE' || columnType === 'DATETIME' || columnType === 'TIMESTAMP'
}

const getEditorDisplayValue = (value: any, column: TableHeader) => {
  if (!isTimeLikeColumn(column) || !column.format || typeof value !== 'string') {
    return value ?? ''
  }

  const trimmed = value.trim()
  if (/^\d{2}:\d{2}:\d{2}$/.test(trimmed) && String(column.format).includes('HH:mm')) {
    return trimmed.slice(0, 5)
  }

  if (/^\d{2}\.\d{2}\.\d{4}\s+\d{2}:\d{2}:\d{2}$/.test(trimmed) && String(column.format).includes('HH:mm')) {
    return trimmed.slice(-8, -3)
  }

  return value
}

const editableDraftKey = () =>
  `${String(props.elementId ?? '')}|${String(props.tableName ?? '')}`

const rememberEditorDraft = (
  rowData: Record<string, any>,
  column: TableHeader,
  value: unknown,
  editorKind: 'text' | 'checkbox',
) => {
  rememberEditableCellDraft(editableDraftKey(), {
    fieldName: String(column.value ?? ''),
    rowIndex: resolveRowIndex(rowData),
    newValue: value,
    editorKind,
  })
}

const onEditorTextInput = (rowData: Record<string, any>, column: TableHeader, event: Event) => {
  const target = event.target as HTMLInputElement | null
  const value = target?.value ?? ''
  rowData[column.value] = value
  rememberEditorDraft(rowData, column, value, 'text')
}

const updateRawRowField = (rawRow: any, fieldName: string, value: any) => {
  const existingField = rawRow?.fields?.[fieldName]

  if (rawRow?.fields && typeof rawRow.fields === 'object') {
    rawRow.fields[fieldName] = existingField?.type
      ? { ...existingField, value }
      : setType(value)
    return
  }

  rawRow[fieldName] = value
}

const buildUpdatedTableValue = (rowIndex: number, fieldName: string, value: any) => {
  const rows = (props.items || []).map((item, index) => {
    const rawRow = item?._rowData
      ? {
          ...item._rowData,
          fields: item._rowData.fields ? { ...item._rowData.fields } : item._rowData.fields,
        }
      : item?.fields
        ? { ...item, fields: { ...item.fields } }
        : { ...item }

    if (index === rowIndex) {
      updateRawRowField(rawRow, fieldName, value)
    }

    return rawRow
  })

  return {
    rows,
    empty: rows.length === 0,
  }
}

const buildRowContext = (rowIndex: number, fieldName: string, updatedTableValue: { rows: any[] }) => {
  const rawRow = updatedTableValue.rows[rowIndex]
  if (!rawRow || typeof rawRow !== 'object') {
    return null
  }

  const flatRow: Record<string, any> = {}
  const fields: Record<string, any> = {}

  if (rawRow.fields && typeof rawRow.fields === 'object') {
    Object.keys(rawRow.fields).forEach((key) => {
      const field = rawRow.fields[key]
      const plainValue = field?.value !== undefined ? field.value : field
      flatRow[key] = plainValue
      fields[key] = field?.type ? { ...field } : setType(plainValue)
    })
  } else {
    Object.keys(rawRow).forEach((key) => {
      if (key === 'fields') return
      flatRow[key] = rawRow[key]
      fields[key] = setType(rawRow[key])
    })
  }

  if (props.tableName) {
    flatRow[`${props.tableName}__rowNum`] = rowIndex
    flatRow[`${props.tableName}__column`] = fieldName
    fields[`${props.tableName}__rowNum`] = { value: rowIndex, type: 'Long' }
    fields[`${props.tableName}__column`] = { value: fieldName, type: 'String' }
  }

  flatRow.fields = fields
  return flatRow
}

const sortIconClass = (column: TableHeader) => {
  if (localMeta.value.sort.column !== column.value) return 'pi-sort-alt'
  return localMeta.value.sort.direction === 'desc' ? 'pi-sort-amount-down-alt' : 'pi-sort-amount-up-alt'
}

const onSort = (column: TableHeader) => {
  if (!column.isSort) return
  const direction =
    localMeta.value.sort.column === column.value && localMeta.value.sort.direction === 'asc'
      ? 'desc'
      : 'asc'

  localMeta.value.sort = { direction, column: column.value }
  emit('update:meta', { ...localMeta.value, sort: { ...localMeta.value.sort } })
  emit('sort', localMeta.value)
}

type PaginationUpdate = {
  page: number
  pages: number
  itemsPerPage?: number
  total?: number
  totalItems?: number
}

const onPaginationChange = (pagination: PaginationUpdate) => {
  const itemsPerPage = pagination.itemsPerPage ?? localMeta.value.itemsPerPage
  const totalItems = pagination.totalItems ?? pagination.total ?? localMeta.value.totalItems

  // Recalculate pages based on totalItems and itemsPerPage
  const calculatedPages = totalItems && itemsPerPage ? Math.ceil(totalItems / itemsPerPage) : 1

  // If itemsPerPage changed, reset to page 1 or adjust if current page is beyond new total
  let newPage = pagination.page
  if (itemsPerPage !== localMeta.value.itemsPerPage) {
    // When rows per page changes, reset to page 1
    newPage = 1
  } else {
    // Ensure page doesn't exceed calculated pages
    newPage = Math.min(Math.max(1, newPage), calculatedPages)
  }

  const updatedMeta: TableMeta = {
    ...localMeta.value,
    page: newPage,
    pages: calculatedPages,
    itemsPerPage,
    totalItems,
    sort: localMeta.value.sort
  }
  localMeta.value = updatedMeta
  emit('update:meta', updatedMeta)
  if( localMeta.value.itemsPerPage !== itemsPerPage ){
    emit('saveSetting', {
      paramName: 'itemsPerPage',
      paramValue: itemsPerPage
    })
  }
}

const recentEditableCommit = { key: '', at: 0 }

const commitCellEdit = (fieldName: string, rowIndex: number, rawValue: unknown): boolean => {
  if (!props.cellEditable || !fieldName) return false
  if (rowIndex < 0 || rowIndex >= props.items.length) return false

  const header = props.headers.find((column) => column.value === fieldName)
  const normalizedValue = isBooleanColumn(header) ? Boolean(rawValue) : rawValue
  const commitKey = `${rowIndex}|${fieldName}|${String(normalizedValue)}`
  const now = Date.now()
  if (recentEditableCommit.key === commitKey && now - recentEditableCommit.at < 400) {
    return true
  }
  recentEditableCommit.key = commitKey
  recentEditableCommit.at = now
  const updatedValue = buildUpdatedTableValue(rowIndex, fieldName, normalizedValue)
  const rowContext = buildRowContext(rowIndex, fieldName, updatedValue)
  const payload = {
    __editableTableUpdate: true,
    value: updatedValue,
    selectedData: rowContext,
    selectedIdx: rowIndex,
    column: fieldName,
  }

  emit('update:modelValue', payload)
  void nextTick(() => {
    emit('change', payload)
  })
  return true
}

const onCellEditComplete = (event: any) => {
  if (!props.cellEditable) {
    return
  }

  const fieldName = event?.field
  if (!fieldName) {
    return
  }

  const rowIndex =
    typeof event?.index === 'number'
      ? event.index
      : resolveRowIndex(event?.data)

  const live = resolvePendingEditableCellCommit(tableRootRef.value, {
    cellEditable: props.cellEditable,
    itemsLength: props.items.length,
    resolveColumnName: resolveColumnNameFromTarget,
  })
  const draft = peekEditableCellDraft(editableDraftKey())
  let newValue = event?.newValue
  if (live && live.fieldName === fieldName && live.editorKind !== 'listbox') {
    newValue = live.newValue
  } else if (
    draft &&
    draft.fieldName === fieldName &&
    (draft.rowIndex === rowIndex || rowIndex < 0) &&
    draft.editorKind !== 'listbox'
  ) {
    newValue = draft.newValue
  }
  clearEditableCellDraft(editableDraftKey())
  commitCellEdit(fieldName, rowIndex, newValue)
}


const onCellEditCancel = () => {
  clearEditableCellDraft(editableDraftKey())
}

const commitPendingCellEdit = (): boolean => {
  if (!props.cellEditable) return false

  const pendingOptions = {
    cellEditable: props.cellEditable,
    itemsLength: props.items.length,
    resolveColumnName: resolveColumnNameFromTarget,
  }
  const live = resolvePendingEditableCellCommit(tableRootRef.value, pendingOptions)
  const draft = peekEditableCellDraft(editableDraftKey())
  const pending = live ?? draft
  if (!pending) return false
  clearEditableCellDraft(editableDraftKey())

  const active = document.activeElement
  if (active instanceof HTMLElement && tableRootRef.value?.contains(active)) {
    active.blur()
  }

  return commitCellEdit(pending.fieldName, pending.rowIndex, pending.newValue)
}

defineExpose({ commitPendingCellEdit })

let clickTimer: ReturnType<typeof setTimeout> | null = null
const DOUBLE_CLICK_DELAY = 250 // ms

const isRowContextActionButtonTarget = (target: HTMLElement | null) =>
  Boolean(target?.closest('.table-row-context-actions__btn'))

const onRowClick = (event: any) => {
  const originalEvent = event.originalEvent || event
  const target = originalEvent?.target as HTMLElement | null

  if (target?.closest('.p-checkbox') || target?.closest('.p-datatable-selectable') || target?.closest('td.p-selection-column, td[data-p-selection-column="true"]') || isRowContextActionButtonTarget(target)) {
    return
  }

  const idx = resolveRowIndex(event.data)
  const originalItem = resolveOriginalRow(event.data, idx)
  const columnName = resolveColumnNameFromTarget(target)
  const header = columnName
    ? props.headers.find((column) => column.value === columnName)
    : undefined

  // Checkbox cells toggle in place. Opening PrimeVue's cell editor swaps the
  // styled box for a native input, and the following row selection paints
  // read-only columns with the editable/selected wash.
  if (header && isEditableColumn(header) && isBooleanColumn(header)) {
    if (clickTimer) {
      clearTimeout(clickTimer)
      clickTimer = null
    }
    const current = readRowBooleanCellValue(event?.data, columnName)
    commitCellEdit(columnName, idx, !current)
    return
  }

  if (isEditableTableCellClick(target)) {
    emit('click', { selectedIdx: idx, column: columnName, data: originalItem })
    return
  }

  // Clear any existing timer
  if (clickTimer) {
    clearTimeout(clickTimer)
    clickTimer = null
  }

  // Delay the selection update to check if it's a double click
  clickTimer = setTimeout(() => {
    clickTimer = null

    // Update selection on single click
    if (props.selectionMode === 'single') {
      internalSelection.value = event.data
    } else if (props.selectionMode === 'multiple' && !originalEvent?.ctrlKey && !originalEvent?.metaKey && !originalEvent?.shiftKey) {
      if (props.withCheckbox) {
        toggleCheckboxRowSelection(event.data)
      } else {
        internalSelection.value = [event.data]
      }
    }

    emit('click', { selectedIdx: idx, column: columnName, data: originalItem })
  }, DOUBLE_CLICK_DELAY)
}

const resolveColumnNameFromTarget = (target: HTMLElement | null | undefined) => {
  const cell = target?.closest('td') as HTMLTableCellElement | null
  if (!cell || typeof cell.cellIndex !== 'number') {
    return ''
  }
  if (cell.classList.contains('table-row-context-actions-col')) {
    return ''
  }

  let headerIndex = cell.cellIndex
  if (props.withCheckbox) {
    headerIndex -= 1
  }
  if (headerIndex < 0) {
    return ''
  }

  const visibleHeaders = props.headers.filter((obj) => selectedColumns.value.includes(obj.value))
  const header = visibleHeaders[headerIndex]
  return header?.value ? String(header.value) : ''
}

const onRowDoubleClick = async (event: any) => {
  // Cancel the single click timer - this is a double click
  if (clickTimer) {
    clearTimeout(clickTimer)
    clickTimer = null
  }

  const originalEvent = event.originalEvent || event
  const target = originalEvent?.target as HTMLElement | null

  if (target?.closest('.p-checkbox') || target?.closest('.p-datatable-selectable') || target?.closest('td.p-selection-column, td[data-p-selection-column="true"]') || isRowContextActionButtonTarget(target)) {
    return
  }

  const idx = resolveRowIndex(event.data)
  const originalItem = resolveOriginalRow(event.data, idx)
  const columnName = resolveColumnNameFromTarget(target)

  const inlineCellAction =
    columnName && originalItem && typeof originalItem === 'object'
      ? originalItem[columnName]?.inlineAction && originalItem[columnName]?.action
        ? originalItem[columnName].action
        : null
      : null

  if (inlineCellAction?.command) {
    emit('action', {
      action: inlineCellAction,
      rowData: originalItem?._rowData || originalItem,
      data: originalItem,
      item: originalItem,
      selectedIdx: idx,
      column: columnName,
    })
    return
  }

  emit('dblclick', { selectedIdx: idx, column: columnName, data: originalItem })
}

const onSaveSettings = () => {
  emit('saveSetting', {
    paramName: 'selectedColumns',
    paramValue: selectedColumns.value
  })
}

const onContextMenu = async (event: any) => {
  const originalEvent: MouseEvent | undefined = event?.originalEvent || event
  const rowDataWithId = event?.data ?? selectedItem.value ?? null
  const idx = resolveRowIndex(rowDataWithId)
  const rowData = resolveOriginalRow(rowDataWithId, idx)
  await handleTableContextMenu(originalEvent, rowData, undefined, { withCheckbox: props.withCheckbox, selectedIdx: idx })
}

const onAction = (actionData: any) => {
  emit('action', actionData)
}

watch(() => props.selected, (newSelected) => {
  isSyncingFromProps = true

  if (!newSelected || newSelected.length === 0) {
    internalSelection.value = props.selectionMode === 'multiple' ? [] : null
    Promise.resolve().then(() => {
      isSyncingFromProps = false
    })
    return
  }

  const findItemWithId = (originalItem: any) => {
    return itemsWithIds.value.find(itemWithId => {
      const original = getOriginalItem(itemWithId)
      return original === originalItem || JSON.stringify(original) === JSON.stringify(originalItem)
    })
  }

  if (props.selectionMode === 'multiple') {
    internalSelection.value = newSelected
      .map(findItemWithId)
      .filter(Boolean)
  } else {
    const found = findItemWithId(newSelected[0])
    internalSelection.value = found || null
  }

  Promise.resolve().then(() => {
    isSyncingFromProps = false
  })
}, { deep: true })
watch(
  () => props.meta,
  (newMeta) => {
    localMeta.value = { ...newMeta }
  },
  { deep: true, flush: 'sync' },
)
</script>

<style scoped lang="scss">
.modern-table {
  width: 100%;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.modern-table--fill {
  display: flex;
  flex-direction: column;
  flex: 1 1 0%;
  /* Floor 2 body rows + thead + chrome; leftover may grow. Do not use 0 (2717). */
  min-height: calc(var(--platform-table-fill-min-body, 120px) + 2.75rem);
  height: 100%;
  overflow: hidden;

  .table-chrome-bar,
  .table-pagination.bottom {
    flex: 0 0 auto;
  }

  /* Swing JScrollPane around JTable: rows (+ sticky thead), not chrome/pager. */
  .modern-table__body-wrap {
    flex: 1 1 0%;
    min-height: var(--platform-table-fill-min-body, 120px);
    display: block;
    overflow: auto;
    scrollbar-gutter: stable;
  }

  :deep(.p-datatable) {
    height: auto;
    min-height: auto;
  }

  :deep(.p-datatable-table-container) {
    overflow: visible !important;
  }

  :deep(.p-datatable-thead) {
    position: sticky;
    top: 0;
    z-index: 2;

    th {
      background: #f4f6f8 !important;
    }
  }
}

.modern-table--full-width {
  :deep(.p-datatable) {
    width: 100%;
    max-width: 100%;
  }

  :deep(.p-datatable-table-container),
  :deep(.p-datatable-wrapper),
  :deep(.p-datatable-scrollable),
  :deep(.p-datatable-scrollable-body),
  :deep(.p-datatable-scrollable-table-container),
  :deep(.p-datatable-scrollable-header),
  :deep(.p-datatable-scrollable-footer) {
    width: 100%;
    max-width: 100%;
  }

  :deep(.p-datatable-table) {
    width: 100% !important;
    min-width: 100%;
    table-layout: fixed;
  }
}

.modern-table--banner-rows {
  width: 100%;

  :deep(.p-datatable-table) {
    width: 100%;
    table-layout: fixed;
  }

  :deep(td.table-listbox-icon-column) {
    padding: 8px 4px;
    vertical-align: middle;
  }

  :deep(td.table-inline-action-fill) {
    width: auto;
    padding: 8px 12px 8px 4px;
    vertical-align: middle;
  }

  :deep(.inline-action-button) {
    min-height: 96px;
  }
}

div.div-table {
  display: table;
  width: 100%;
}

div.div-table-cell {
  background:#f8fafc;
  display: table-cell;
  vertical-align: middle;
}

i.pointer {
  cursor: pointer;
}

/* стили PrimeVue DataTable */
::v-deep(.p-datatable) {
  --p-checkbox-background: #ffffff;

  .p-datatable-contextmenu-row-selected {
    outline: none;
  }

  .p-datatable-thead {
    background: #f4f6f8;

    th {
      background: transparent !important;
      border: 1px solid #e5e7eb;
      color: #374151;
      font-weight: 600;
      /*text-transform: uppercase;*/
      letter-spacing: 0.5px;
      padding: 1rem;
      vertical-align: top;

      &.p-sortable-column:not(.p-sorting-disabled):hover {
        background: rgba(0, 0, 0, 0.03) !important;
      }

      &.p-highlight {
        background: rgba(0, 0, 0, 0.06) !important;
      }

      /* Тема PrimeVue: selected header = primary фон + белая иконка.
         Фон у нас светлый — иконка должна быть как текст шапки. */
      .p-datatable-sort-icon,
      .p-sortable-column-icon {
        color: #374151;
        fill: currentColor;
      }
    }
  }

  .p-datatable-tbody {
    background: #f4f6f8; // Same light background as header

    tr {
      transition: background-color 0.2s ease;
      cursor: pointer;
      background: #f4f6f8;

      &:nth-child(odd),
      &:nth-child(even) {
        background: #f4f6f8;
      }

      &:hover {
        background: #e8ecf0;
      }

      &.p-highlight {
        background: #dde7f0;

        &:hover {
          background: #d0dde8;
        }
      }

      td {
        border-color: #e5e7eb;
        padding: 1rem;
        color: #374151;
        background: transparent;
        vertical-align: top;

        // Swing TableStripedDelegate.editable — rgb(255, 255, 210)
        &.table-column-editable {
          background-color: #ffffd2 !important;
        }
      }

      &:hover td.table-column-editable {
        background-color: #f0f0c4 !important;
      }

      &.p-highlight td.table-column-editable {
        background-color: #e6e6b8 !important;

        &:hover {
          background-color: #dedead !important;
        }
      }
    }
  }
}

::v-deep(.p-datatable.table-without-header) {
  .p-datatable-thead {
    display: none;
  }
}

:deep(.p-datatable-table > thead > tr > th.p-selection-column),
:deep(.p-datatable-table > tbody > tr > td.p-selection-column),
:deep(.p-datatable-table > thead > tr > th[data-p-selection-column="true"]),
:deep(.p-datatable-table > tbody > tr > td[data-p-selection-column="true"]) {
  width: 40px !important;
  min-width: 40px !important;
  max-width: 40px !important;
  box-sizing: border-box;
  vertical-align: middle;
  padding-left: 0.75rem;
  padding-right: 0.35rem;
  overflow: hidden;
}

:deep(.p-datatable-table > tbody > tr > td.p-selection-column + td),
:deep(.p-datatable-table > tbody > tr > td[data-p-selection-column="true"] + td) {
  padding-left: 0.5rem;
}

/* Same as MCheckbox: hide PrimeVue native input so only the styled box shows. */
:deep(.p-checkbox) {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  flex-shrink: 0;
}

:deep(.p-checkbox-input) {
  appearance: none;
  opacity: 0;
  position: absolute;
  inset: 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  width: 100%;
  min-width: 0;
}

:deep(.p-datatable-column-header-content) {
  width: 100%;
  min-width: 0;
}

:deep(th.m-table-col-resizable) {
  position: relative;
}

:deep(th.m-table-col-resizable .m-table-column-resizer) {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 8px;
  margin: 0;
  transform: translateX(50%);
  cursor: col-resize;
  z-index: 2;
  touch-action: none;
}

.header-content.inline-actions-header {
  justify-content: center;
  cursor: default;
}

.header-text {
  font-size: 0.875rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow-wrap: normal;
  word-break: normal;
  line-height: 1.25;
}

@media (max-width: 768px) {
  .modern-table__body-wrap {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .modern-table--full-width {
    :deep(.p-datatable-table) {
      table-layout: auto !important;
      width: max-content !important;
      min-width: 100%;
    }
  }

  .header-text {
    white-space: nowrap;
    overflow-wrap: normal;
    word-break: normal;
  }

  :deep(.p-datatable-table > tbody > tr > td .cell-content > *) {
    overflow-wrap: normal;
    word-break: normal;
    white-space: nowrap;
  }

  .table-pagination {
    padding: 0.375rem 0.5rem;

    &.bottom {
      overflow-x: auto;
    }
  }
}

:deep(.p-datatable-table > tbody > tr > td .cell-content) {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

:deep(.p-datatable-table > tbody > tr > td .cell-content > *) {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow-wrap: normal;
  word-break: normal;
}

:deep(td[data-type='MultiLineText'] .cell-content),
:deep(td[data-type='MULTILINETEXT'] .cell-content),
:deep(td[data-type='multilinetext'] .cell-content),
:deep(td[data-type='MultiLineText'] .cell-content > *),
:deep(td[data-type='MULTILINETEXT'] .cell-content > *),
:deep(td[data-type='multilinetext'] .cell-content > *) {
  overflow: visible;
  text-overflow: unset;
}

.modern-table--remote-sort {
  :deep(.p-datatable-sort-icon),
  :deep(.p-sortable-column-icon) {
    display: none !important;
  }
}

.sort-icon {
  flex: 0 0 auto;
  font-size: 0.75rem;
  color: #374151;
  opacity: 0.55;
  transition: opacity 0.2s ease;
}

.header-content--sortable .sort-icon.pi-sort-amount-up-alt,
.header-content--sortable .sort-icon.pi-sort-amount-down-alt {
  opacity: 1;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
  padding: 1rem 1rem;
  color: #6b7280;

  .empty-icon {
    font-size: 1.5rem;
    opacity: 0.35;
  }

  .empty-text {
    margin: 0;
    font-size: 0.8125rem;
    font-weight: 500;
    color: #6b7280;
  }
}

.modern-table--empty {
  :deep(.p-datatable-table-container),
  :deep(.p-datatable-wrapper),
  :deep(.p-datatable-scrollable-body) {
    overflow: hidden !important;
    max-height: none !important;
    height: auto !important;
  }

  :deep(.p-datatable-tbody > tr > td) {
    border-bottom: none;
  }
}

.table-chrome-bar {
  width: 100%;
}

.table-chrome-bar--summary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;

  .table-pagination {
    flex: 1 1 auto;
    min-width: 0;
  }
}

.table-expand-rows-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  background: #f8fafc;
  color: #475569;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: #0f172a;
    background: #e2e8f0;
  }

  .pi {
    font-size: 0.9rem;
  }
}

.table-expand-rows-btn--active {
  border-color: #64748b;
  background: #e2e8f0;
  color: #0f172a;
}

.modern-table--rows-expanded :deep(td[data-type='MultiLineText'] .cell-content),
.modern-table--rows-expanded :deep(td[data-type='MULTILINETEXT'] .cell-content),
.modern-table--rows-expanded :deep(td[data-type='multilinetext'] .cell-content) {
  align-items: flex-start;
  white-space: normal;
}

.modern-table--rows-expanded :deep(td[data-type='MultiLineText'] .cell-content > *),
.modern-table--rows-expanded :deep(td[data-type='MULTILINETEXT'] .cell-content > *),
.modern-table--rows-expanded :deep(td[data-type='multilinetext'] .cell-content > *) {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.modern-table__body-wrap {
  position: relative;
  min-height: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
}

.table-settings-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: #4b5563;
    background: #f3f4f6;
  }

  .pi-cog {
    font-size: 0.9375rem;
  }
}

/* Desktop: cog overlays last header cell */
.table-settings-btn--thead {
  position: absolute;
  top: 0.45rem;
  right: 0.5rem;
  z-index: 5;
}

.table-settings-btn--in-actions {
  position: static;
  width: 1.25rem;
  height: 1.25rem;

  .pi-cog {
    font-size: 0.8125rem;
  }
}

/* Keep sort icon clear of absolute settings cog (themes must not override via padding shorthand). */
.modern-table__body-wrap:has(.table-settings-btn--thead) :deep(.p-datatable-thead > tr > th:last-child:not(.table-row-context-actions-col)) {
  padding-right: 2.75rem !important;
}

:deep(th.table-row-context-actions-col),
:deep(td.table-row-context-actions-col) {
  position: sticky;
  right: 0;
  box-sizing: border-box;
  overflow: visible;
}

:deep(th.table-row-context-actions-col) {
  z-index: 4;
  background: #f4f6f8 !important;
  padding: 1rem 0.2rem !important;
  vertical-align: top;
  box-shadow: -4px 0 8px rgba(15, 23, 42, 0.06);
}

:deep(th.table-row-context-actions-col .p-datatable-column-header-content) {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
}

:deep(td.table-row-context-actions-col) {
  z-index: 1;
  background: #f4f6f8 !important;
  padding: 0.2rem 0.2rem !important;
  vertical-align: middle !important;
  text-align: right;
  box-shadow: -4px 0 8px rgba(15, 23, 42, 0.06);
}

:deep(.p-datatable-tbody > tr:hover > td.table-row-context-actions-col) {
  background: #e8ecf0 !important;
}

:deep(.p-datatable-tbody > tr.p-highlight > td.table-row-context-actions-col) {
  background: #dde7f0 !important;
}

:deep(.p-datatable-tbody > tr.p-highlight:hover > td.table-row-context-actions-col) {
  background: #d0dde8 !important;
}

.table-pagination {
  padding: 0.5rem 0.75rem;
  background: #ffffff;

  &.top {
    padding: 0;
    border: none;
    background: transparent;
  }

  &.bottom {
    border-top: 1px solid #e5e7eb;
  }
}

.table-cell-editor {
  width: 100%;
  min-width: 100px;
  padding: 0.375rem 0.5rem;
  border: 1px solid #93c5fd;
  border-radius: 4px;
  background: #fff;
  color: #111827;
}

.table-cell-editor-checkbox {
  width: 16px;
  height: 16px;
}

:deep(.table-cell-editor-listbox) {
  width: 100%;
  min-width: 120px;
}

:deep(.table-cell-editor-listbox-select) {
  width: 100%;
}

.modern-table.skip-default-styles {
  :deep(.p-datatable) {
    height: auto;
    min-height: auto;
  }

  :deep(.p-datatable-table-container) {
    overflow: visible !important;
  }

  :deep(.p-datatable-thead) {
    position: sticky;
    top: 0;
    z-index: 2;

    th {
      background: transparent !important;
      border: none !important;
      padding: 0 !important;
    }
  }

  :deep(.p-datatable-tbody) {
    tr {
      td {
        background: transparent !important;
        border: none !important;
        padding: 0 !important;
      }

      &.p-highlight {
        background: transparent !important;

        td {
          background: transparent !important;
        }
      }

      &:hover {
        background: transparent !important;

        td {
          background: transparent !important;
        }
      }
    }
  }

  :deep(.p-datatable-table > thead > tr > th),
  :deep(.p-datatable-table > tbody > tr > td) {
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
  }

  :deep(.p-checkbox) {
    display: none;
  }

  :deep(.p-selection-column) {
    display: none;
  }
}
</style>
